// skills/gatherer/gathererUI.js
// Gatherer UI rendering and interaction

import { gathererSkill } from './gatherer.js';
import { GATHERING_ZONES, GATHERING_TOOLS, TIER_DROPS, FOOD_YIELDS } from './gathererData.js';
import { gameState } from '../../game/gameState.js';
import { SKILLS, GAME_CONFIG } from '../../game/constants.js';
import logger from '../../utils/logger.js';
import { IS_DEV } from '../../config/env.js';
import { adminPanel } from '../admin/admin.js';
import { getCurrentSkillId } from '../../core/routeSetup.js';
import { getItemName, getItemIcon } from '../../data/items/index.js';
import { formatPercent, formatWithCommas, formatNumber } from '../../utils/format.js';
import { toastWarning } from '../../ui/toast.js';
import { updateToolUpgradeCost } from '../../ui/uiHelpers.js';
import { getCorruptionSpeedMultiplier, getCorruptionLootMultiplier, getCorruptionXPMultiplier } from '../../systems/corruption.js';
import { getXPPerHour } from '../../systems/experience.js';
import { renderSkillInfo } from '../../ui/skillInfo.js';
import { renderTalentButton, isTalentTreeOpen } from '../../systems/talentTree/talentTreeUI.js';
import { getTalentXPBonus } from '../../systems/talentTree/talentTree.js';
import { getSkillIcon } from '../../core/skillRegistry.js';

// Current tab state
let currentTab = 'tier1';

// Track loot multiplier to detect corruption changes
let lastLootMultiplier = 1;

/**
 * Render compact skill header with integrated tool upgrade
 */
function renderCompactHeader() {
    const skill = gameState.getSkill(SKILLS.GATHERER);
    const xpPerHour = getXPPerHour(SKILLS.GATHERER);

    // Calculate display values
    const isMax = skill.level >= GAME_CONFIG.MAX_LEVEL;
    const virtualEnabled = gameState.settings.virtualLevelsEnabled;
    const isVirtualMax = skill.virtualLevel >= GAME_CONFIG.VIRTUAL_LEVEL_CAP;

    let levelDisplay, progressPercent, progressText;

    if (isMax && virtualEnabled) {
        levelDisplay = `${skill.level} (${skill.virtualLevel})`;
        progressPercent = isVirtualMax ? 100 : (skill.currentXp / skill.nextLevelXp * 100);
        progressText = isVirtualMax ? 'Virtual Max' : `${formatWithCommas(skill.currentXp)} / ${formatWithCommas(skill.nextLevelXp)}`;
    } else if (isMax) {
        levelDisplay = `${skill.level} (Max)`;
        progressPercent = 100;
        progressText = 'Mastered';
    } else {
        levelDisplay = skill.level;
        progressPercent = (skill.currentXp / skill.nextLevelXp * 100);
        progressText = `${formatWithCommas(skill.currentXp)} / ${formatWithCommas(skill.nextLevelXp)}`;
    }

    // Get tool info
    const tools = Object.values(GATHERING_TOOLS);
    const highestOwned = gameState.getHighestPurchasedTool(SKILLS.GATHERER, tools);
    const activeTool = highestOwned || tools[0];
    const activeMultiplier = activeTool?.speedMultiplier || 1.0;
    const goldCount = gameState.getGold();

    // Find next upgrade
    const allToolsSorted = [...tools].sort((a, b) => a.level - b.level);
    const nextUpgrade = allToolsSorted.find(tool =>
        tool.price > 0 && !gameState.hasPurchasedTool(SKILLS.GATHERER, tool.id)
    );

    // Build upgrade HTML
    let upgradeHtml = '';
    if (nextUpgrade) {
        const isLocked = skill.level < nextUpgrade.level;
        const canAfford = goldCount >= nextUpgrade.price;

        if (isLocked) {
            upgradeHtml = `
                <span class="compact-upgrade locked">
                    → ${nextUpgrade.name} (${nextUpgrade.speedMultiplier}x)
                    <span class="upgrade-cost ${canAfford ? 'affordable' : 'cant-afford'}" data-price="${nextUpgrade.price}">${getItemIcon('coins')} ${formatNumber(nextUpgrade.price)}</span>
                    <span class="upgrade-lock">🔒 ${skill.level}/${nextUpgrade.level}</span>
                </span>
            `;
        } else {
            upgradeHtml = `
                <span class="compact-upgrade ${canAfford ? 'available' : ''}"
                      onclick="window.gathererUI.upgradeTool('${nextUpgrade.id}')">
                    → ${nextUpgrade.name} (${nextUpgrade.speedMultiplier}x)
                    <span class="upgrade-cost ${canAfford ? 'affordable' : 'cant-afford'}" data-price="${nextUpgrade.price}">${getItemIcon('coins')} ${formatNumber(nextUpgrade.price)}</span>
                </span>
            `;
        }
    } else {
        upgradeHtml = `<span class="compact-upgrade maxed">✓ All tools</span>`;
    }

    return `
        <div class="compact-header">
            <div class="compact-header-row">
                <div class="compact-title">
                    <span class="compact-icon">${getSkillIcon(SKILLS.GATHERER)}</span>
                    <span class="compact-name">Gatherer</span>
                    ${renderSkillInfo(SKILLS.GATHERER)}
                    ${renderTalentButton(SKILLS.GATHERER)}
                    <span class="compact-level">Lv ${levelDisplay}</span>
                </div>
                <div class="compact-stats">
                    <span class="compact-stat xp-total">
                        <span class="stat-label">Total XP:</span>
                        <span class="stat-value">${formatWithCommas(skill.xp)}</span>
                    </span>
                    <span class="compact-stat xp-rate">
                        <span class="stat-label">XP/h:</span>
                        <span class="stat-value">${formatWithCommas(xpPerHour)}</span>
                    </span>
                </div>
                <div class="compact-tool">
                    <span class="tool-label">Tool:</span>
                    <span class="tool-current">${activeTool.name} (${activeMultiplier}x)</span>
                    ${upgradeHtml}
                </div>
            </div>
            <div class="compact-progress">
                <div class="compact-progress-bar">
                    <div class="compact-progress-fill" style="width: ${progressPercent}%"></div>
                    <span class="compact-progress-percent">${Math.floor(progressPercent)}%</span>
                </div>
                <span class="compact-progress-text">${progressText} XP</span>
            </div>
        </div>
    `;
}

/**
 * Check if any zone in a tier is unlocked
 */
function isTierUnlocked(tierNum, playerLevel) {
    const zones = Object.values(GATHERING_ZONES);
    const tierZones = zones.filter(z => z.tier === tierNum);
    return tierZones.some(z => playerLevel >= z.level);
}

/**
 * Render tab buttons with lock icons for locked tiers
 */
function renderTabs() {
    const skill = gameState.getSkill(SKILLS.GATHERER);
    const playerLevel = skill.level;

    const tier1Unlocked = isTierUnlocked(1, playerLevel);
    const tier2Unlocked = isTierUnlocked(2, playerLevel);
    const tier3Unlocked = isTierUnlocked(3, playerLevel);

    return `
        <div class="skill-tabs">
            <button class="skill-tab ${currentTab === 'tier1' ? 'active' : ''}"
                    onclick="window.gathererUI.switchTab('tier1')">
                🌱 Tier 1${tier1Unlocked ? '' : ' 🔒'}
            </button>
            <button class="skill-tab ${currentTab === 'tier2' ? 'active' : ''}"
                    onclick="window.gathererUI.switchTab('tier2')">
                🌿 Tier 2${tier2Unlocked ? '' : ' 🔒'}
            </button>
            <button class="skill-tab ${currentTab === 'tier3' ? 'active' : ''}"
                    onclick="window.gathererUI.switchTab('tier3')">
                ✨ Tier 3${tier3Unlocked ? '' : ' 🔒'}
            </button>
        </div>
    `;
}

/**
 * Switch tab
 */
export function switchTab(tab) {
    currentTab = tab;
    renderGathererUI();
}

/**
 * Render the gatherer interface
 */
export function renderGathererUI(params = {}) {
    // Update current tab if specified in params
    if (params.tab) {
        currentTab = params.tab;
    }

    const container = document.getElementById('skill-content');
    const skill = gameState.getSkill(SKILLS.GATHERER);

    container.innerHTML = `
    <div class="gatherer-interface">
      ${renderCompactHeader()}

      <div class="gatherer-content">
        ${renderTabs()}

        <div class="tab-content">
            ${renderZoneGrid(skill.level)}
        </div>
      </div>
    </div>
  `;
}

/**
 * Render the zone selection grid for the current tier
 */
function renderZoneGrid(playerLevel) {
    const currentAction = gameState.getCurrentAction();
    const zones = Object.values(GATHERING_ZONES);

    // Get tier number from current tab
    const tierNum = currentTab === 'tier1' ? 1 : currentTab === 'tier2' ? 2 : 3;
    const tierZones = zones.filter(z => z.tier === tierNum);

    // Get tier info
    const tierInfo = {
        1: { name: 'Basic Materials', food: 'Basic Pet Food', description: 'Entry-level zones for gathering basic pet food and Twisted Root Fiber.' },
        2: { name: 'Refined Materials', food: 'Refined Pet Food', description: 'Mid-level zones for gathering refined pet food and Living Arcane Husk.' },
        3: { name: 'Condensed Materials', food: 'Condensed Pet Food', description: 'Advanced zones for gathering condensed pet food and Arcane Core Fragments.' }
    };

    const info = tierInfo[tierNum];

    return `
        <div class="zone-grid-container">
            <div class="tier-header">
                <h3 class="section-title">${info.name}</h3>
                <p class="tier-description">${info.description}</p>
            </div>

            <div class="zone-grid">
                ${tierZones.map(zone => renderZoneCard(zone, playerLevel, currentAction)).join('')}
            </div>
        </div>
    `;
}

/**
 * Render a single zone card
 */
function renderZoneCard(zone, playerLevel, currentAction) {
    const isUnlocked = playerLevel >= zone.level;
    const isActive = currentAction?.type === 'gatherer' && currentAction?.zone?.id === zone.id;
    const progress = isActive ? currentAction.progress : 0;

    // Get drop info for this zone's tier
    const tierDrops = TIER_DROPS[zone.tier];
    const yieldConfig = FOOD_YIELDS[zone.foodYield];
    const foodName = getItemName(tierDrops.food);
    const foodIcon = getItemIcon(tierDrops.food);
    const rareName = getItemName(tierDrops.rare);
    const rareIcon = getItemIcon(tierDrops.rare);
    const bonusDrops = tierDrops.bonusFood || [];

    // Calculate effective time with all buffs
    const speedMultiplier = gathererSkill.getEffectiveSpeedMultiplier();
    const corruptionSpeedMult = getCorruptionSpeedMultiplier(SKILLS.GATHERER);
    const corruptionXPMult = getCorruptionXPMultiplier(SKILLS.GATHERER);
    const totalSpeedMultiplier = speedMultiplier * corruptionSpeedMult;
    const effectiveTime = zone.gatherTime / totalSpeedMultiplier;

    // Calculate effective XP with corruption then talent
    const talentXPBonus = getTalentXPBonus(SKILLS.GATHERER);
    let effectiveXP = zone.xp;
    if (corruptionXPMult > 1) effectiveXP = Math.floor(effectiveXP * corruptionXPMult);
    if (talentXPBonus > 0) effectiveXP = Math.floor(effectiveXP * (1 + talentXPBonus));

    // Loot multiplier for drop quantities
    const lootMultiplier = getCorruptionLootMultiplier(SKILLS.GATHERER);

    // Calculate boosted yields
    const boostedMin = Math.floor(yieldConfig.min * lootMultiplier);
    const boostedMax = Math.floor(yieldConfig.max * lootMultiplier);
    const yieldDisplay = lootMultiplier > 1
        ? `<span class="drop-qty boosted">${boostedMin}-${boostedMax}</span>`
        : `<span class="drop-qty">${yieldConfig.min}-${yieldConfig.max}</span>`;

    const cardClasses = [
        'zone-card',
        isUnlocked ? '' : 'locked',
        isActive ? 'active' : ''
    ].filter(Boolean).join(' ');

    return `
        <div class="${cardClasses}"
             data-zone-id="${zone.id}"
             style="--zone-color: ${zone.color}"
             onclick="${isUnlocked ? `window.gathererUI.toggleGathering('${zone.id}')` : ''}">
            <div class="zone-header">
                <span class="zone-level">${isUnlocked ? `Lvl ${zone.level}` : `Req: ${zone.level}`}</span>
                <span class="zone-card-icon">🌿</span>
                <span class="zone-name">${zone.name}</span>
            </div>

            <div class="zone-info">
                <div class="zone-stat">
                    <span class="stat-icon gatherer-icon"></span>
                    <span class="stat-label">XP:</span>
                    <span class="stat-value">${effectiveXP}</span>
                </div>
                <div class="zone-stat">
                    <span class="stat-icon time-icon"></span>
                    <span class="stat-label">Time:</span>
                    <span class="stat-value ${corruptionSpeedMult > 1 ? 'boosted' : ''}">${(effectiveTime / 1000).toFixed(1)}s</span>
                </div>
            </div>

            <div class="zone-drops">
                <div class="drop-info food-drop">
                    <span class="drop-name">${foodIcon} ${foodName}</span>
                    ${yieldDisplay}
                </div>
                <div class="bonus-drops-section">
                    <span class="bonus-label">Bonus Drops:</span>
                    ${bonusDrops.map(bonus => {
                        const bonusIcon = getItemIcon(bonus.itemId);
                        const bonusName = getItemName(bonus.itemId);
                        return `
                            <div class="drop-info bonus-drop">
                                <span class="drop-name">${bonusIcon} ${bonusName}</span>
                                <span class="drop-chance">${formatPercent(bonus.chance)}</span>
                            </div>
                        `;
                    }).join('')}
                    ${tierDrops.bonusMaterials && tierDrops.bonusMaterials.items.length > 0 ?
                        tierDrops.bonusMaterials.items.map(mat => {
                            const matIcon = getItemIcon(mat.itemId);
                            const matName = getItemName(mat.itemId);
                            return `
                                <div class="drop-info bonus-drop material">
                                    <span class="drop-name">${matIcon} ${matName}</span>
                                    <span class="drop-chance">${formatPercent(tierDrops.bonusMaterials.chance)}</span>
                                </div>
                            `;
                        }).join('')
                    : ''}
                    <div class="drop-info rare-drop">
                        <span class="drop-name" title="${rareName}">${rareIcon} ${rareName}</span>
                        <span class="drop-chance">${formatPercent(zone.rareChance)}</span>
                    </div>
                </div>
            </div>

            <div class="zone-action">
                ${isUnlocked ? `
                    <button class="action-btn ${isActive ? 'active' : ''}">
                        ${isActive ? 'Stop' : 'Gather'}
                    </button>
                ` : `
                    <span class="locked-text">Locked</span>
                `}
            </div>

            <div class="zone-progress">
                <div class="zone-progress-fill" style="width: ${progress * 100}%"></div>
            </div>
        </div>
    `;
}

/**
 * Toggle gathering on/off for a zone
 */
export function toggleGathering(zoneId) {
    const currentAction = gameState.getCurrentAction();

    if (currentAction && currentAction.zone?.id === zoneId) {
        gathererSkill.stopGathering();
    } else {
        // Check if we can gather first to get the reason if not
        const canResult = gathererSkill.canGather(zoneId);
        if (!canResult.success) {
            toastWarning(canResult.reason);
        } else {
            gathererSkill.startGathering(zoneId);
        }
    }

    renderGathererUI();
}

/**
 * Upgrade tool
 */
export function upgradeTool(toolId) {
    const result = gathererSkill.upgradeTool(toolId);

    if (result.success) {
        renderGathererUI();
    } else {
        toastWarning(result.reason);
    }
}

/**
 * Update gatherer UI each tick
 */
export function updateGathererUI() {
    // Update compact header stats
    updateCompactHeader();

    // Update tab lock icons based on current level
    updateTabLockIcons();

    // Check if loot multiplier changed (corruption started/ended)
    const currentLootMultiplier = getCorruptionLootMultiplier(SKILLS.GATHERER);
    if (currentLootMultiplier !== lastLootMultiplier) {
        lastLootMultiplier = currentLootMultiplier;
        if (!isTalentTreeOpen()) {
            renderGathererUI();
        }
        return;
    }

    // Update action progress if gathering is active
    const action = gameState.getCurrentAction();
    if (!action || action.type !== 'gatherer') return;

    const activeCard = document.querySelector(`.zone-card[data-zone-id="${action.zone.id}"]`);
    if (activeCard) {
        const progressFill = activeCard.querySelector('.zone-progress-fill');
        if (progressFill) {
            progressFill.style.width = `${action.progress * 100}%`;
        }
    }
}

/**
 * Update compact header stats without full re-render
 */
function updateCompactHeader() {
    const skill = gameState.getSkill(SKILLS.GATHERER);
    const xpPerHour = getXPPerHour(SKILLS.GATHERER);

    // Update XP values
    const xpTotalEl = document.querySelector('.gatherer-interface .compact-stat.xp-total .stat-value');
    if (xpTotalEl) xpTotalEl.textContent = formatWithCommas(skill.xp);

    const xpRateEl = document.querySelector('.gatherer-interface .compact-stat.xp-rate .stat-value');
    if (xpRateEl) xpRateEl.textContent = formatWithCommas(xpPerHour);

    // Update level display
    const isMax = skill.level >= GAME_CONFIG.MAX_LEVEL;
    const virtualEnabled = gameState.settings.virtualLevelsEnabled;
    const isVirtualMax = skill.virtualLevel >= GAME_CONFIG.VIRTUAL_LEVEL_CAP;

    let levelDisplay, progressPercent, progressText;

    if (isMax && virtualEnabled) {
        levelDisplay = `${skill.level} (${skill.virtualLevel})`;
        progressPercent = isVirtualMax ? 100 : (skill.currentXp / skill.nextLevelXp * 100);
        progressText = isVirtualMax ? 'Virtual Max' : `${formatWithCommas(skill.currentXp)} / ${formatWithCommas(skill.nextLevelXp)}`;
    } else if (isMax) {
        levelDisplay = `${skill.level} (Max)`;
        progressPercent = 100;
        progressText = 'Mastered';
    } else {
        levelDisplay = skill.level;
        progressPercent = (skill.currentXp / skill.nextLevelXp * 100);
        progressText = `${formatWithCommas(skill.currentXp)} / ${formatWithCommas(skill.nextLevelXp)}`;
    }

    const levelEl = document.querySelector('.gatherer-interface .compact-level');
    if (levelEl) levelEl.textContent = `Lv ${levelDisplay}`;

    // Update progress bar
    const progressFill = document.querySelector('.gatherer-interface .compact-progress-fill');
    if (progressFill) progressFill.style.width = `${progressPercent}%`;

    const progressPercentEl = document.querySelector('.gatherer-interface .compact-progress-percent');
    if (progressPercentEl) progressPercentEl.textContent = `${Math.floor(progressPercent)}%`;

    const progressTextEl = document.querySelector('.gatherer-interface .compact-progress-text');
    if (progressTextEl) progressTextEl.textContent = `${progressText} XP`;

    // Update tool upgrade gold color
    updateToolUpgradeCost('.gatherer-interface');
}

/**
 * Update tab lock icons without full re-render
 */
function updateTabLockIcons() {
    const skill = gameState.getSkill(SKILLS.GATHERER);
    const playerLevel = skill.level;

    const tabButtons = document.querySelectorAll('.gatherer-interface .skill-tab');
    if (tabButtons.length !== 3) return;

    const tiers = [1, 2, 3];
    const icons = ['🌱', '🌿', '✨'];

    tabButtons.forEach((btn, index) => {
        const tierNum = tiers[index];
        const isUnlocked = isTierUnlocked(tierNum, playerLevel);
        const expectedText = `${icons[index]} Tier ${tierNum}${isUnlocked ? '' : ' 🔒'}`;

        if (btn.textContent.trim() !== expectedText.trim()) {
            btn.textContent = expectedText;
        }
    });
}

/**
 * Setup gatherer skill listeners
 */
export function initGathererUI() {
    gathererSkill.initializeStartingTool();

    gathererSkill.onGatheringComplete = (zone, xpResult, drops) => {
        showNotification(`Gathered in ${zone.name}! +${zone.xp} XP`);

        // Show rare drop notification
        const tierDrops = TIER_DROPS[zone.tier];
        const gotRare = drops.some(d => d.itemId === tierDrops.rare);
        if (gotRare) {
            const rareName = getItemName(tierDrops.rare);
            showNotification(`Rare drop: ${rareName}!`, 'success');
        }

        if (xpResult.leveledUp) {
            showNotification(`Gatherer level ${xpResult.newLevel}!`, 'success');
        }

        // Only re-render if gatherer page is currently being viewed and talent tree is not open
        if (getCurrentSkillId() === SKILLS.GATHERER && !isTalentTreeOpen()) {
            // Skip full re-render at high speeds to prevent button flickering
            const speedMult = IS_DEV ? adminPanel.state.global.speedMultiplier : 1;
            if (speedMult <= 1) {
                renderGathererUI();
            }
        }
    };
}

function showNotification(message, type = 'info') {
    logger.game(message);
}

if (typeof window !== 'undefined') {
    window.gathererUI = {
        toggleGathering,
        upgradeTool,
        switchTab
    };
}

export default {
    renderGathererUI,
    toggleGathering,
    upgradeTool,
    switchTab,
    updateGathererUI,
    initGathererUI
};
